import { KeyRound, Loader2, ShieldCheck, User as UserIcon, Wallet } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { setWallets, updateProfile } from '@/api/member';

const ProfileSubpage = ({ me, onRefresh }) => {
  const [username, setUsername] = useState(me.username || '');
  const [email, setEmail] = useState(me.email || '');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [ltc, setLtc] = useState(me.wallet_addresses?.LTC || '');
  const [trx, setTrx] = useState(me.wallet_addresses?.TRX || '');
  const [doge, setDoge] = useState(me.wallet_addresses?.DOGE || '');
  const [savingWallets, setSavingWallets] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile({
        username: username !== me.username ? username : undefined,
        email: email !== me.email ? email : undefined,
        current_password: currentPwd || undefined,
        new_password: newPwd || undefined,
      });
      toast.success('Profile updated');
      setCurrentPwd('');
      setNewPwd('');
      await onRefresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveWallets = async (e) => {
    e.preventDefault();
    setSavingWallets(true);
    try {
      await setWallets({ LTC: ltc, TRX: trx, DOGE: doge });
      toast.success('Withdrawal wallets saved');
      await onRefresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSavingWallets(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="subpage-profile">
      {/* Profile + password */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-7" data-testid="profile-section">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center border"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
          >
            <UserIcon className="w-5 h-5 text-[#FFC20E]" />
          </div>
          <div>
            <div className="font-display font-bold text-lg md:text-xl">Profile &amp; Security</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Account information · password</div>
          </div>
        </div>
        <form onSubmit={handleSaveProfile} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Username">
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required className="sc-input" data-testid="profile-username" />
          </Field>
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="sc-input" data-testid="profile-email" />
          </Field>
          <Field
            label={
              <span className="inline-flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" />
                Current Password
              </span>
            }
          >
            <input
              type="password"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              placeholder={me.has_password ? 'required to change password' : '— no password set yet —'}
              className="sc-input"
              data-testid="profile-current-pwd"
            />
          </Field>
          <Field
            label={
              <span className="inline-flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" />
                New Password (min 8)
              </span>
            }
          >
            <input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="leave blank to keep current"
              className="sc-input"
              data-testid="profile-new-pwd"
            />
          </Field>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={savingProfile} className="sc-btn-white" data-testid="profile-save">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Save Profile
            </button>
          </div>
        </form>
      </section>

      {/* Wallet addresses */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-7" data-testid="wallets-section">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center border"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
          >
            <Wallet className="w-5 h-5 text-[#FFC20E]" />
          </div>
          <div>
            <div className="font-display font-bold text-lg md:text-xl">Withdrawal Wallet Addresses</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">LTC · TRX · DOGE</div>
          </div>
        </div>
        <p className="text-[13px] text-[var(--sc-text-dim)] mt-3 max-w-2xl">
          Save your permanent on-chain destinations. Payouts settle directly to these addresses when you submit a withdrawal request.
        </p>
        <form onSubmit={handleSaveWallets} className="mt-5 grid grid-cols-1 gap-4">
          {[
            { sym: 'LTC', value: ltc, setter: setLtc, hint: 'L... / M... / ltc1...' },
            { sym: 'TRX', value: trx, setter: setTrx, hint: 'T... (TRC20)' },
            { sym: 'DOGE', value: doge, setter: setDoge, hint: 'D...' },
          ].map((w) => (
            <Field
              key={w.sym}
              label={
                <span className="inline-flex items-center gap-2">
                  <span
                    className="inline-flex w-7 h-7 rounded-md items-center justify-center font-display font-bold text-[10px]"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,182,18,0.18), rgba(255,182,18,0.02))',
                      border: '1px solid rgba(255, 182, 18, 0.32)',
                      color: '#FFE08A',
                    }}
                  >
                    {w.sym}
                  </span>
                  {w.sym} Address
                </span>
              }
            >
              <input
                type="text"
                value={w.value}
                onChange={(e) => w.setter(e.target.value)}
                placeholder={w.hint}
                className="sc-input sc-tabular"
                data-testid={`wallet-${w.sym.toLowerCase()}`}
              />
            </Field>
          ))}
          <div className="flex justify-end">
            <button type="submit" disabled={savingWallets} className="sc-btn-white" data-testid="wallets-save">
              {savingWallets ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
              Save Wallet Addresses
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: every caller passes a single <input> as children, which this label wraps
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default ProfileSubpage;
